import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { auth } from "../../firebase";

const BookingFailedModal = ({
  onClose,
  bookingId,
  paymentStatus = "Success",
  bookingStatus = "Failed",
  ticketData = null,
  source = "chat"
}) => {
  const navigate = useNavigate();
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportForm, setSupportForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });

  const openSupportModal = () => {
    const user = auth.currentUser;

    // Extract journey details if available
    const itinerary = ticketData?.ticket_details?.FlightItinerary ||
      ticketData?.Response?.Response?.FlightItinerary ||
      ticketData?.booking_request?.FlightItinerary ||
      ticketData?.FlightItinerary;

    let segments = itinerary?.Segments || [];
    if (segments.length > 0 && Array.isArray(segments[0])) {
      segments = segments[0];
    }

    const firstSeg = segments[0] || {};
    const lastSeg = segments[segments.length - 1] || firstSeg;

    const origin = firstSeg?.Origin?.Airport?.CityName || firstSeg?.Origin?.Airport?.AirportCode || 'Origin';
    const dest = lastSeg?.Destination?.Airport?.CityName || lastSeg?.Destination?.Airport?.AirportCode || 'Destination';
    const pnr = itinerary?.PNR || ticketData?.pnr || 'N/A';

    setSupportForm({
      firstName: '',
      lastName: '',
      email: user?.email || '',
      phone: user?.phoneNumber?.replace("+91", "") || '',
      message: `I need help with my booking from ${origin} to ${dest} (PNR: ${pnr}).`
    });
    setShowSupportModal(true);
  };

  if (showSupportModal) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-[4px]"
          onClick={() => setShowSupportModal(false)}
        ></div>
        <div className="bg-white rounded-[24px] w-full max-w-[700px] relative z-10 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
          {/* Close Button */}
          <button
            onClick={() => setShowSupportModal(false)}
            className="absolute top-6 right-6 p-2 text-slate-400 hover:text-slate-600 transition-colors rounded-full hover:bg-slate-50 z-20"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div className="px-10 py-6">
            <div className="text-center mb-6">
              <h2 className="text-[32px] font-bold text-[#101828] mb-1 tracking-tight">Customer query</h2>
              <p className="text-[#475467] text-base">We'd love to hear from you. Please fill out this form.</p>
            </div>

            <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); setShowSupportModal(false); }}>
              <div className="grid grid-cols-2 gap-x-6">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-[#344054]">First name</label>
                  <input
                    type="text"
                    placeholder="First name"
                    value={supportForm.firstName}
                    onChange={(e) => setSupportForm({ ...supportForm, firstName: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-[#D0D5DD] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] focus:ring-4 focus:ring-slate-100 focus:border-slate-500 outline-none transition-all placeholder:text-[#667085] text-[#101828] font-medium"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-[#344054]">Last name</label>
                  <input
                    type="text"
                    placeholder="Last name"
                    value={supportForm.lastName}
                    onChange={(e) => setSupportForm({ ...supportForm, lastName: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-[#D0D5DD] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] focus:ring-4 focus:ring-slate-100 focus:border-slate-500 outline-none transition-all placeholder:text-[#667085] text-[#101828] font-medium"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#344054]">Email</label>
                <input
                  type="email"
                  placeholder="you@company.com"
                  value={supportForm.email}
                  onChange={(e) => setSupportForm({ ...supportForm, email: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border border-[#D0D5DD] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] focus:ring-4 focus:ring-slate-100 focus:border-slate-500 outline-none transition-all placeholder:text-[#667085] text-[#101828] font-medium"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#344054]">Phone number</label>
                <div className="flex border border-[#D0D5DD] rounded-lg shadow-[0px_1px_2px_rgba(16,24,40,0.05)] overflow-hidden focus-within:ring-4 focus-within:ring-slate-100 focus-within:border-slate-500 transition-all">
                  <div className="flex items-center gap-1 px-4 bg-white border-r border-[#D0D5DD] cursor-pointer hover:bg-slate-50 transition-colors">
                    <span className="text-sm font-medium text-[#101828]">IN</span>
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#667085]">
                      <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <input
                    type="tel"
                    placeholder="+91 00000 00000"
                    value={supportForm.phone}
                    onChange={(e) => setSupportForm({ ...supportForm, phone: e.target.value })}
                    className="w-full px-4 py-2.5 outline-none placeholder:text-[#667085] text-[#101828] font-medium text-sm"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#344054]">Message</label>
                <textarea
                  placeholder="Include a message..."
                  rows="3"
                  value={supportForm.message}
                  onChange={(e) => setSupportForm({ ...supportForm, message: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border border-[#D0D5DD] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] focus:ring-4 focus:ring-slate-100 focus:border-slate-500 outline-none transition-all placeholder:text-[#667085] text-[#101828] font-medium resize-none text-sm"
                ></textarea>
              </div>



              <div className="pt-3">
                <button
                  type="submit"
                  className="w-full py-3.5 rounded-lg text-base font-bold transition-all shadow-sm bg-[#1A2233] text-white hover:bg-slate-800 active:scale-[0.99]"
                >
                  Send message
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-10 max-w-xl w-full mx-4 relative overflow-hidden">
        {/* Background Decor */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-orange-50 rounded-full -mr-16 -mt-16 opacity-50"></div>

        {/* Close Button */}
        <button
          onClick={() => {
            if (source === "search") {
              navigate("/");
            } else {
              navigate("/chat");
            }
          }}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100 z-10"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="flex flex-col">
          {/* Header Section */}
          <div className="flex items-start gap-4 mb-8">
            <div className="flex flex-col items-center gap-1 relative pt-1">
              {/* Timeline Line */}
              <div className="absolute top-8 bottom-2 w-0.5 bg-gray-100 left-1/2 -translate-x-1/2"></div>

              {/* Step 1: Payment Success */}
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center z-10 shadow-sm shadow-green-200">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="h-10"></div>

              {/* Step 2: Booking Fail */}
              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center z-10 shadow-sm shadow-red-200">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div className="h-6"></div>

              {/* Step 3: Future */}
              <div className="w-8 h-8 rounded-full bg-white border-2 border-gray-200 z-10"></div>
            </div>

            <div className="flex-1 pt-1">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 leading-none mb-2">Payment success</h2>
                <p className="text-gray-500 text-sm">Your payment has been processed successfully.</p>
              </div>

              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 leading-none mb-2">Ticket Booking Failed</h2>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Unfortunately, we were unable to complete your ticket booking despite the successful payment.
                </p>
              </div>
            </div>
          </div>

          {/* Details Box */}
          <div className="bg-gray-50 rounded-xl p-5 mb-8 border border-gray-100">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Booking Details</div>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Booking ID:</span>
                <span className="font-mono font-medium text-gray-800">{bookingId || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Payment Status:</span>
                <span className="font-semibold text-green-600">{paymentStatus}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Booking Status:</span>
                <span className="font-semibold text-red-500">{bookingStatus}</span>
              </div>
            </div>
          </div>

          {/* Important Notice for Refund */}
          <div className="mt-2 mb-8 bg-blue-50/50 p-4 rounded-lg border border-blue-100">
            <p className="text-xs text-blue-700 leading-normal">
              <span className="font-bold">Don't worry:</span> If any extra amount was captured, it will be automatically refunded to your original payment method within 5-7 business days.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">

            <button
              onClick={openSupportModal}
              className="flex-1 bg-white border-2 border-gray-200 text-gray-700 py-3.5 px-6 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-300 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Contact Support
            </button>
            <button
              onClick={() => {
                if (source === "search") {
                  navigate("/");
                } else {
                  navigate("/chat");
                }
              }}
              className="flex-1 bg-[#1A2233] text-white py-3.5 px-6 rounded-xl font-semibold hover:bg-slate-800 transition-all active:scale-[0.98] shadow-lg shadow-gray-200"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

BookingFailedModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  bookingId: PropTypes.string,
  paymentStatus: PropTypes.string,
  bookingStatus: PropTypes.string,
  ticketData: PropTypes.object,
};

export default BookingFailedModal;
